import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useFormContext, Controller } from "react-hook-form";

// Import airflow images
import airflowP2a from "../../../utils/resources/KK_P_Left_Top_aCrop.svg";
import airflowP2s from "../../../utils/resources/KK_P_Left_Top_sCrop.svg";
import airflowP4a from "../../../utils/resources/KK_P_Right_Top_aCrop.svg";
import airflowP4s from "../../../utils/resources/KK_P_Right_Top_sCrop.svg";
import airflowR1a from "../../../utils/resources/KK_R_Left_Bottom_aCrop.svg";
import airflowR1s from "../../../utils/resources/KK_R_Left_Bottom_sCrop.svg";
import airflowR2a from "../../../utils/resources/KK_R_Left_Top_a-cropped.svg";
import airflowR2s from "../../../utils/resources/KK_R_Left_Top_s-cropped.svg";
import airflowR3a from "../../../utils/resources/KK_R_Right_Bottom_aCrop.svg";
import airflowR3s from "../../../utils/resources/KK_R_Right_Bottom_sCrop.svg";
import airflowR4a from "../../../utils/resources/KK_R_Right_Top_a-cropped.svg";
import airflowR4s from "../../../utils/resources/KK_R_Right_Top_s-cropped.svg";
import airflowE2a from "../../../utils/resources/KK_E_Left_Top_aCrop.svg";
import airflowE2s from "../../../utils/resources/KK_E_Left_Top_sCrop.svg";
import airflowE4a from "../../../utils/resources/KK_E_Right_Top_aCrop.svg";
import airflowE4s from "../../../utils/resources/KK_E_Right_Top_sCrop.svg";
import blackArrow from "../../../utils/resources/blackArrow.svg";
import blueArrow from "../../../utils/resources/blueArrow.svg";
import energyAirFlowLeft from "../../../utils/resources/energyAirFlowLeft.svg";
import energyAirFlowRight from "../../../utils/resources/energyAirFlowRight.svg";
import energyAirFlowLeftActive from "../../../utils/resources/energyAirFlowLeftActive.svg";
import energyAirFlowRightActive from "../../../utils/resources/energyAirFlowRightActive.svg";
import verticalAHU1Selected from "../../../utils/resources/Vertical_AHU_1_selected.svg";
import verticalAHU1Unselected from "../../../utils/resources/Vertical_AHU_1_unselected.svg";
import verticalAHU2Selected from "../../../utils/resources/Vertical_AHU_2_selected.svg";
import verticalAHU2Unselected from "../../../utils/resources/Vertical_AHU_2_unselected.svg";

import { useSelector } from "react-redux";
import { AhuType } from "../../../models/unitType";
import { brandTranslation } from "../../../utils/brandUtils";

import energyStandardChart from "../../../image/energy-standard-chart.png";
import energyGeniusChart from "../../../image/energy-genius-chart.png";

// MUI components
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import FormHelperText from "@mui/material/FormHelperText";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";

const TypeOfUnit = () => {
  const { t } = useTranslation();
  const params = useParams();
  const { control, watch, setValue, trigger } = useFormContext();
  const { isClivet } = useSelector((state) => state.brand);

  // Get baseUnitType from route params
  const baseUnitType = params?.unitType ? parseInt(params.unitType, 10) : null;
  const isLinePrime = baseUnitType === AhuType.LINE_PRIME;

  // Watch form values
  const unitType = watch("Type");
  const coils = watch("coils");
  const heatRecovery = watch("heatRecovery");
  const heatRecoveryPlateType = watch("heatRecoveryPlateType");
  const airflowDirection = watch("airflowDirection");
  const sizeOfUnit = watch("sizeOfUnit");
  const sizeOfUnitTypes = watch("sizeOfUnitTypes");

  const [active, setActive] = useState(airflowDirection || 0);
  const [infoAnchorEl, setInfoAnchorEl] = useState(null);

  // Options arrays
  const installationOptions = [
    {
      value: "installationIndoor",
      label: t("unitTypeList.installationIndoor"),
    },
    ...(unitType !== AhuType.COMPACT_VERTICAL
      ? [
        {
          value: "installationOutdoor",
          label: t("unitTypeList.installationOutdoor"),
        },
      ]
      : []),
  ];

  const coilsOptions = useMemo(() => {
    if (isLinePrime) {
      return [
        { value: "without", label: t("linePrime.withoutCoils", { defaultValue: "Without coils" }) },
        { value: "cooler", label: t("linePrime.cooler", { defaultValue: "Cooler" }) },
      ];
    }
    const options = [
      { value: "without", label: t("unitType.without") },
      { value: "heater", label: t("unitType.heater") },
      { value: "cooler", label: t("unitType.cooler") },
      {
        value: "both",
        label: t("unitType.heater") + " & " + t("unitType.cooler"),
      },
    ];
    if (baseUnitType === 2) {
      options.push({
        value: "reversible_refrigerant",
        label: "With a reversible refrigerant heat pump and water heater",
      });
    }
    return options;
  }, [baseUnitType, isLinePrime, t]);

  // Filter heat recovery options based on baseUnitType
  const heatRecoveryOptions = useMemo(() => {
    const allOptions = [
      {
        value: "Rotary heat exchanger",
        label: t("unitTypeList.Rotary heat exchanger"),
      },
      {
        value: "Plate heat exchanger",
        label: t("unitTypeList.Plate heat exchanger"),
      },
      {
        value: "Polypropylene with indirect adiabatic cooling",
        label: "Polypropylene heat recovery with indirect adiabatic cooling",
      },
      {
        value: "Polypropylene",
        label: "Polypropylene heat recovery",
      },
    ];

    // Exclude Polypropylene options for COMPACT (baseUnitType === 1)
    if (baseUnitType === 1) {
      return allOptions.filter(
        (option) =>
          option.value !== "Polypropylene with indirect adiabatic cooling" &&
          option.value !== "Polypropylene",
      );
    }

    return allOptions;
  }, [baseUnitType, t]);

  const unitTypePlateOptions = [
    { value: "Plate", label: t("unitType.Condensing") },
    { value: "Enthalpy", label: t("unitType.Enthalpy") },
  ];

  const unitDivisionOptions = [
    { value: "transport1", label: t("unitTypeList.transport1") },
    { value: "transport3", label: t("unitTypeList.transport3") },
  ];

  const transport1SizeIds = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 96, 97, 98, 99, 100, 102, 103, 105, 106, 107];

  useEffect(() => {
    const unitType = sizeOfUnitTypes.find((u) => u.id === sizeOfUnit);
    if (unitType) {
      setValue("heatRecoverySize", unitType.heatRecoverySize);
      setValue("fan", unitType.fan);
      setValue("coilsWidth", unitType.coilsWidth);
      setValue("coilsHeight", unitType.coilsHeight);
    }
  }, [sizeOfUnit, sizeOfUnitTypes, setValue]);

  // Determine unit type options based on baseUnitType
  const getUnitTypeOptions = () => {
    if (isLinePrime || baseUnitType === 1) {
      return [];
    } else if (baseUnitType === 2) {
      const energyOptions = [
        {
          value: AhuType.ENERGY_PRIME_STANDARD,
          label: t(brandTranslation("unitType.energyPrimeStandard")),
        },
      ];
      if (!isClivet) {
        energyOptions.push({
          value: AhuType.ENERGY_PRIME_GENIUS,
          label: "Energy:Prime Genius",
        });
      }
      return energyOptions;
    } else if (baseUnitType === 4) {
      return [
        {
          value: AhuType.AQUA_PRIME_STANDARD,
          label: "Aqua:Prime Standard",
          disabled: true,
        },
        {
          value: AhuType.AQUA_PRIME_GENIUS,
          label: "Aqua:Prime Genius",
          disabled: false,
        },
      ];
    }
    return [{ value: "Compact: PRO", label: "Compact: PRO" }];
  };

  const unitTypeOptions = getUnitTypeOptions();

  // Helper function to determine coils configuration
  const getCoilsConfig = () => {
    if (isLinePrime) {
      return {
        disabled: false,
        value: coils,
        displayValue: coils,
      };
    }
    if (baseUnitType === 1) {
      return {
        disabled: false,
        value: coils,
        displayValue: coils,
      };
    } else if (baseUnitType === 2) {
      if (unitType === AhuType.ENERGY_PRIME_STANDARD) {
        return {
          disabled: true,
          value: "both",
          displayValue: "With water heater and cooler",
        };
      } else if (unitType === AhuType.ENERGY_PRIME_GENIUS) {
        return {
          disabled: true,
          value: "DX",
          displayValue:
            "With a reversible refrigerant heat pump and water heater",
        };
      }
      return {
        disabled: true,
        value: coils || "",
        displayValue: "*Choose Unit Type first*",
      };
    } else if (baseUnitType === 4) {
      if (unitType === AhuType.AQUA_PRIME_GENIUS) {
        return {
          disabled: true,
          value: "DX",
          displayValue:
            "With a reversible refrigerant heat pump and water heater",
        };
      }
      return {
        disabled: true,
        value: coils || "",
        displayValue: "*Choose Unit Type first*",
      };
    }
    return {
      disabled: false,
      value: coils,
      displayValue: coils,
    };
  };

  // Helper function to determine heat recovery configuration
  const getHeatRecoveryConfig = () => {
    if (isLinePrime) {
      return {
        disabled: true,
        value: "Without heat recovery",
        displayValue: t("linePrime.withoutHeatRecovery", { defaultValue: "Without heat recovery" }),
      };
    }
    // Compact:Prime Vertical always uses Plate heat exchanger (forced, not editable)
    if (baseUnitType === 1 && unitType === AhuType.COMPACT_VERTICAL) {
      return {
        disabled: true,
        value: "Plate heat exchanger",
        displayValue: t("unitTypeList.Plate heat exchanger"),
      };
    }
    if (baseUnitType === 1) {
      return {
        disabled: false,
        value: heatRecovery,
        displayValue: heatRecovery,
      };
    } else if (baseUnitType === 2) {
      return {
        disabled: true,
        value: "Polypropylene with indirect adiabatic cooling",
        displayValue:
          "Polypropylene heat recovery with indirect adiabatic cooling",
      };
    } else if (baseUnitType === 4) {
      return {
        disabled: true,
        value: "Polypropylene",
        displayValue: "Polypropylene heat recovery",
      };
    }
    return {
      disabled: false,
      value: heatRecovery,
      displayValue: heatRecovery,
    };
  };

  const coilsConfig = getCoilsConfig();
  const heatRecoveryConfig = getHeatRecoveryConfig();

  // Filter unit types based on heat recovery
  const unitTypesToShow = useMemo(() => {
    if (!sizeOfUnitTypes || sizeOfUnitTypes.length === 0) {
      return [];
    }

    // LINE:PRIME shows all sizes from API response (backend filters for LINE:PRIME)
    if (isLinePrime) {
      return sizeOfUnitTypes;
    }

    if (heatRecovery === "Rotary heat exchanger") {
      return sizeOfUnitTypes.filter((u) => u.heatRecoveryType === "Rotary");
    } else if (
      heatRecovery === "Plate heat exchanger" &&
      heatRecoveryPlateType === "Enthalpy"
    ) {
      return sizeOfUnitTypes.filter((u) => u.heatRecoveryType === "Enthalpy");
    } else if (
      heatRecovery === "Plate heat exchanger" &&
      heatRecoveryPlateType === "Plate"
    ) {
      return sizeOfUnitTypes.filter((u) => u.heatRecoveryType === "Plate");
    } else if (
      heatRecovery === "Polypropylene with indirect adiabatic cooling" ||
      heatRecovery === "Polypropylene"
    ) {
      return sizeOfUnitTypes.filter(
        (unit) => unit.heatRecoveryType === "PPL_4" && unit.type === unitType,
      );
    }

    return [];
  }, [sizeOfUnitTypes, heatRecovery, heatRecoveryPlateType, unitType, isLinePrime]);

  // Validation rules
  const validationRules = useMemo(
    () => ({
      installation: {
        required: t("projectinformation.err") || "This field is required",
      },
      Type: {
        required:
          baseUnitType !== 1 && !isLinePrime
            ? t("projectinformation.err") || "This field is required"
            : false,
      },
      coils: {
        required: t("projectinformation.err") || "This field is required",
      },
      heatRecovery: {
        required: isLinePrime
          ? false
          : t("projectinformation.err") || "This field is required",
      },
      heatRecoveryPlateType: {
        required:
          heatRecovery === "Plate heat exchanger" &&
            !heatRecoveryConfig.disabled
            ? t("projectinformation.err") || "This field is required"
            : false,
      },
      ecoDesign: {
        required: t("projectinformation.err") || "This field is required",
      },
      casingQuality: {
        required: t("projectinformation.err") || "This field is required",
      },
      sizeOfUnit: {
        validate: (value) => {
          if (unitTypesToShow.length === 0) {
            return "Please correct your airflow to get more options";
          }
          if (!value) {
            return t("projectinformation.err") || "This field is required";
          }
          return true;
        },
      },
      airflowDirection: {
        required:
          !isLinePrime && heatRecovery !== "" && heatRecovery !== "None"
            ? t("projectinformation.err") || "This field is required"
            : false,
      },
      servicingSide: {
        required: isLinePrime
          ? t("projectinformation.err") || "This field is required"
          : false,
      },
    }),
    [
      t,
      baseUnitType,
      isLinePrime,
      heatRecovery,
      heatRecoveryConfig.disabled,
      unitTypesToShow.length,
    ],
  );

  // Handle unitType change - auto-set coils if locked
  const handleUnitTypeChange = (value) => {
    setValue("Type", value, { shouldValidate: true });

    if (baseUnitType === 2) {
      if (value === AhuType.ENERGY_PRIME_STANDARD) {
        setValue("coils", "both", { shouldValidate: true });
      } else if (value === AhuType.ENERGY_PRIME_GENIUS) {
        setValue("coils", "DX", {
          shouldValidate: true,
        });
      }
    } else if (baseUnitType === 4) {
      if (value === AhuType.AQUA_PRIME_GENIUS) {
        setValue("coils", "DX", {
          shouldValidate: true,
        });
      }
    }
  };

  // Handle airflow direction change
  const handleAirflowDirectionChange = (number) => {
    setValue("airflowDirection", number, { shouldValidate: true });
    setActive(number);
  };

  // Auto-set locked values when baseUnitType or unitType changes
  useEffect(() => {
    const currentHeatRecovery = watch("heatRecovery");
    if (heatRecoveryConfig.disabled && heatRecoveryConfig.value) {
      if (heatRecoveryConfig.value !== currentHeatRecovery) {
        setValue("heatRecovery", heatRecoveryConfig.value, {
          shouldValidate: true,
        });
      }
    }
  }, [
    baseUnitType,
    unitType,
    heatRecoveryConfig.disabled,
    heatRecoveryConfig.value,
    watch,
    setValue,
  ]);

  useEffect(() => {
    const currentCoils = watch("coils");
    if (coilsConfig.disabled && coilsConfig.value) {
      if (
        coilsConfig.value !== "*Choose Unit Type first*" &&
        coilsConfig.value !== "" &&
        coilsConfig.value !== currentCoils
      ) {
        setValue("coils", coilsConfig.value, { shouldValidate: true });
      }
    }
  }, [
    baseUnitType,
    unitType,
    coilsConfig.disabled,
    coilsConfig.value,
    watch,
    setValue,
  ]);

  // Auto-set LINE:PRIME Type and heatRecovery
  useEffect(() => {
    if (isLinePrime) {
      setValue("Type", AhuType.LINE_PRIME, { shouldValidate: false });
      setValue("heatRecovery", "Without heat recovery", { shouldValidate: false });
      setValue("airflowDirection", 1, { shouldValidate: false });
    }
  }, [isLinePrime, setValue]);

  // Sync active state with form value
  useEffect(() => {
    setActive(airflowDirection || 1);
  }, [airflowDirection]);

  // Trigger validation for sizeOfUnit when unitTypesToShow changes
  useEffect(() => {
    trigger("sizeOfUnit");
  }, [unitTypesToShow.length, trigger]);

  return (
    <Container component="main" sx={{ mb: 4, "min-width": "100%" }}>
      <Paper
        variant="outlined"
        sx={{ p: { xs: 2, md: "15px 24px 24px 24px" }, align: "center" }}
      >
        <Box
          sx={{ mt: 1, display: "flex", flexDirection: "column", gap: "16px" }}
        >
          <Box
            sx={{ display: "flex", justifyContent: "center", width: "100%" }}
          >
            <Typography
              sx={{ textAlign: "center", fontSize: "38px", mb: "0px" }}
            >
              {t("mainform.typeOfUnit")}
            </Typography>
          </Box>

          <Controller
            name="installation"
            control={control}
            rules={validationRules.installation}
            render={({ field, fieldState: { error } }) => (
              <FormControl
                fullWidth
                required
                variant="outlined"
                sx={{ mt: 2 }}
                error={!!error}
              >
                <InputLabel
                  id="select-label-installation"
                  sx={{ color: "#373437", backgroundColor: "white", px: 0.5 }}
                  shrink
                >
                  {t("unitType.label")}
                </InputLabel>
                <Select
                  {...field}
                  labelId="select-label-installation"
                  id="select-installation"
                  label={t("unitType.label")}
                  displayEmpty
                  renderValue={(selected) =>
                    selected
                      ? installationOptions.find(
                        (opt) => opt.value === selected,
                      )?.label
                      : "*Choose an option (required)*"
                  }
                  MenuProps={{ disableScrollLock: true }}
                  disablePortal
                  sx={{ mb: 1 }}
                >
                  {installationOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />

          <Controller
            name="coils"
            control={control}
            rules={validationRules.coils}
            render={({ field, fieldState: { error } }) => (
              <FormControl
                fullWidth
                required
                variant="outlined"
                sx={{ mt: 2 }}
                error={!!error}
              >
                <InputLabel
                  id="select-label-coils"
                  sx={{ color: "#373437", backgroundColor: "white", px: 0.5 }}
                  shrink
                >
                  {t("unitType.coils")}
                </InputLabel>
                <Select
                  {...field}
                  labelId="select-label-coils"
                  id="select-coils"
                  label={t("unitType.coils")}
                  displayEmpty
                  disabled={coilsConfig.disabled}
                  renderValue={(selected) => {
                    if (coilsConfig.disabled && coilsConfig.displayValue) {
                      return coilsConfig.displayValue;
                    }
                    const displayValue = selected
                      ? coilsOptions.find((opt) => opt.value === selected)
                        ?.label
                      : null;
                    return (
                      displayValue ||
                      coilsConfig.displayValue ||
                      "*Choose an option (required)*"
                    );
                  }}
                  MenuProps={{ disableScrollLock: true }}
                  disablePortal
                  sx={{ mb: 1 }}
                >
                  {coilsOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />

          <Controller
            name="heatRecovery"
            control={control}
            rules={validationRules.heatRecovery}
            render={({ field, fieldState: { error } }) => (
              <FormControl
                fullWidth
                required
                variant="outlined"
                sx={{ mt: 2 }}
                error={!!error}
              >
                <InputLabel
                  id="select-label-hr"
                  htmlFor="select-heat-recovery"
                  sx={{ color: "#373437", backgroundColor: "white", px: 0.5 }}
                  shrink
                >
                  {t("unitType.label3")}
                </InputLabel>
                <Select
                  {...field}
                  labelId="select-label-hr"
                  id="select-heat-recovery"
                  label={t("unitType.label3")}
                  displayEmpty
                  disabled={heatRecoveryConfig.disabled}
                  renderValue={(selected) => {
                    if (
                      heatRecoveryConfig.disabled &&
                      heatRecoveryConfig.displayValue
                    ) {
                      return heatRecoveryConfig.displayValue;
                    }
                    const displayValue = selected
                      ? heatRecoveryOptions.find(
                        (opt) => opt.value === selected,
                      )?.label
                      : null;
                    return (
                      displayValue ||
                      heatRecoveryConfig.displayValue ||
                      "*Choose an option (required)*"
                    );
                  }}
                  MenuProps={{ disableScrollLock: true }}
                  disablePortal
                  sx={{ mb: 1 }}
                >
                  {heatRecoveryOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />

          {heatRecovery === "Plate heat exchanger" &&
            (!heatRecoveryConfig.disabled || unitType === AhuType.COMPACT_VERTICAL) && (
              <Controller
                name="heatRecoveryPlateType"
                control={control}
                rules={validationRules.heatRecoveryPlateType}
                render={({ field, fieldState: { error } }) => (
                  <FormControl fullWidth error={!!error}>
                    <InputLabel
                      id="select-label7"
                      sx={{ mt: 1, color: "#373437" }}
                    >
                      {t("unitType.heatRecoveryPlateType")}
                    </InputLabel>
                    <Select
                      {...field}
                      disablePortal
                      MenuProps={{ disableScrollLock: true }}
                      fullWidth
                      sx={{ mt: 1, mb: 1 }}
                      labelId="select-label7"
                      label={t("unitType.heatRecoveryPlateType")}
                      displayEmpty
                      renderValue={(selected) =>
                        selected
                          ? unitTypePlateOptions.find((opt) => opt.value === selected)?.label
                          : "*Choose an option (required)*"
                      }
                    >
                      {unitTypePlateOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {error && <FormHelperText>{error.message}</FormHelperText>}
                  </FormControl>
                )}
              />
            )}

          {/* LINE:PRIME Servicing Side dropdown */}
          {isLinePrime && (
            <Controller
              name="servicingSide"
              control={control}
              rules={validationRules.servicingSide}
              render={({ field, fieldState: { error } }) => (
                <FormControl
                  fullWidth
                  required
                  variant="outlined"
                  sx={{ mt: 2 }}
                  error={!!error}
                >
                  <InputLabel
                    id="select-label-servicing-side"
                    sx={{ color: "#373437", backgroundColor: "white", px: 0.5 }}
                    shrink
                  >
                    {t("linePrime.servicingSide", { defaultValue: "Servicing side:" })}
                  </InputLabel>
                  <Select
                    {...field}
                    labelId="select-label-servicing-side"
                    id="select-servicing-side"
                    label={t("linePrime.servicingSide", { defaultValue: "Servicing side:" })}
                    displayEmpty
                    renderValue={(selected) =>
                      selected
                        ? selected === "right"
                          ? t("linePrime.servicingSideRight", { defaultValue: "Right" })
                          : t("linePrime.servicingSideLeft", { defaultValue: "Left" })
                        : "*Choose an option (required)*"
                    }
                    MenuProps={{ disableScrollLock: true }}
                    disablePortal
                    sx={{ mb: 1 }}
                  >
                    <MenuItem value="right">
                      {t("linePrime.servicingSideRight", { defaultValue: "Right" })}
                    </MenuItem>
                    <MenuItem value="left">
                      {t("linePrime.servicingSideLeft", { defaultValue: "Left" })}
                    </MenuItem>
                  </Select>
                  {error && (
                    <FormHelperText error>{error.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />
          )}

          {/* Airflow direction images - hidden for LINE:PRIME */}
          {!isLinePrime && (
            <Box className="airflow-direction-images">
              {heatRecovery ===
                "Polypropylene with indirect adiabatic cooling" && (
                  <Box className="airflow-option" sx={{ display: "flex", mt: 3, mb: 3 }}>
                    <Box sx={{ m: 1 }}>
                      <img
                        src={
                          active === 1 ? energyAirFlowLeftActive : energyAirFlowLeft
                        }
                        alt="airflow left"
                        onClick={() => handleAirflowDirectionChange(1)}
                      />
                    </Box>
                    <Box sx={{ m: 1 }}>
                      <img
                        src={
                          active === 2
                            ? energyAirFlowRightActive
                            : energyAirFlowRight
                        }
                        alt="airflow right"
                        onClick={() => handleAirflowDirectionChange(2)}
                      />
                    </Box>
                  </Box>
                )}
              {heatRecovery === "Rotary heat exchanger" && (
                <Box sx={{ display: "flex", mt: 3, mb: 3 }}>
                  <Box sx={{ m: 2 }}>
                    <img
                      src={active === 1 ? airflowR1a : airflowR1s}
                      alt="1"
                      onClick={() => handleAirflowDirectionChange(1)}
                    />
                  </Box>
                  <Box sx={{ m: 2 }}>
                    <img
                      src={active === 2 ? airflowR3a : airflowR3s}
                      alt="2"
                      onClick={() => handleAirflowDirectionChange(2)}
                    />
                  </Box>
                  <Box sx={{ m: 2 }}>
                    <img
                      src={active === 3 ? airflowR2a : airflowR2s}
                      alt="3"
                      onClick={() => handleAirflowDirectionChange(3)}
                    />
                  </Box>
                  <Box sx={{ m: 2 }}>
                    <img
                      src={active === 4 ? airflowR4a : airflowR4s}
                      alt="4"
                      onClick={() => handleAirflowDirectionChange(4)}
                    />
                  </Box>
                </Box>
              )}
              {heatRecovery === "Plate heat exchanger" &&
                heatRecoveryPlateType === "Plate" &&
                unitType !== AhuType.COMPACT_VERTICAL && (
                  <Box sx={{ display: "flex", mt: 3, mb: 3 }}>
                    <Box sx={{ m: 1 }}>
                      <img
                        src={active === 1 ? airflowP2a : airflowP2s}
                        alt="2"
                        onClick={() => handleAirflowDirectionChange(1)}
                      />
                    </Box>
                    <Box sx={{ m: 1 }}>
                      <img
                        src={active === 2 ? airflowP4a : airflowP4s}
                        alt="2"
                        onClick={() => handleAirflowDirectionChange(2)}
                      />
                    </Box>
                  </Box>
                )}
              {heatRecovery === "Plate heat exchanger" &&
                heatRecoveryPlateType === "Enthalpy" &&
                unitType !== AhuType.COMPACT_VERTICAL && (
                  <Box sx={{ display: "flex", mt: 3, mb: 3 }}>
                    <Box sx={{ m: 1 }}>
                      <img
                        src={active === 1 ? airflowE2a : airflowE2s}
                        alt="1"
                        onClick={() => handleAirflowDirectionChange(1)}
                      />
                    </Box>
                    <Box sx={{ m: 1 }}>
                      <img
                        src={active === 2 ? airflowE4a : airflowE4s}
                        alt="2"
                        onClick={() => handleAirflowDirectionChange(2)}
                      />
                    </Box>
                  </Box>
                )}
              {heatRecovery === "Plate heat exchanger" &&
                unitType === AhuType.COMPACT_VERTICAL &&
                heatRecoveryPlateType && (
                  <Box sx={{ display: "flex", mt: 3, mb: 3 }}>
                    <Box sx={{ m: 1 }}>
                      <img
                        src={active === 1 ? verticalAHU1Selected : verticalAHU1Unselected}
                        alt="1"
                        onClick={() => handleAirflowDirectionChange(1)}
                        style={{ height: "14em", width: "90%" }}
                      />
                    </Box>
                    <Box sx={{ m: 1 }}>
                      <img
                        src={active === 2 ? verticalAHU2Selected : verticalAHU2Unselected}
                        alt="2"
                        onClick={() => handleAirflowDirectionChange(2)}
                        style={{ height: "14em", width: "90%" }}
                      />
                    </Box>
                  </Box>
                )}
              <Box sx={{ order: "-1" }}>
                <Typography
                  sx={{
                    textAlign: "left",
                    marginBottom: "10px",
                    marginTop: { xs: "25px", lg: "0" },
                    fontSize: { xs: "14px" },
                    paddingLeft: "15px",
                    fontWeight: "bold",
                  }}
                >
                  {t("typeofunits.legend")}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <Box
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <img
                      src={blueArrow}
                      alt="blue arrow"
                      style={{ width: "40%" }}
                    />
                    <Typography sx={{ fontSize: { xs: "14px", lg: "16px" } }}>
                      {t("operatingpoint.supplyair")}
                    </Typography>
                  </Box>
                  <Box style={{ display: "flex", alignItems: "center" }}>
                    <img
                      src={blackArrow}
                      alt="black arrow"
                      style={{ width: "40%" }}
                    />
                    <Typography sx={{ fontSize: { xs: "14px", lg: "16px" } }}>
                      {t("operatingpoint.exhaustAir")}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          {/* <Controller
            name="ecoDesign"
            control={control}
            rules={validationRules.ecoDesign}
            render={({ field, fieldState: { error } }) => (
              <FormControl
                fullWidth
                required
                variant="outlined"
                sx={{ mt: 2 }}
                error={!!error}
              >
                <InputLabel
                  id="select-label-ecodesign"
                  sx={{ color: "#373437", backgroundColor: "white", px: 0.5 }}
                  shrink
                >
                  {t("requirementsTextFields.label")}
                </InputLabel>
                <Select
                  {...field}
                  labelId="select-label-ecodesign"
                  id="select-ecodesign"
                  label={t("requirementsTextFields.label")}
                  displayEmpty
                  renderValue={(selected) =>
                    selected
                      ? t(`requirementsList.${selected}`)
                      : "*Choose an option (required)*"
                  }
                  MenuProps={{ disableScrollLock: true }}
                  disablePortal
                  sx={{ mb: 1 }}
                >
                  <MenuItem value="compliant">
                    {t("requirementsList.compliant")}
                  </MenuItem>
                  <MenuItem value="notCompliant">
                    {t("requirementsList.notCompliant")}
                  </MenuItem>
                </Select>
              </FormControl>
            )}
          /> */}

          <Controller
            name="casingQuality"
            control={control}
            rules={validationRules.casingQuality}
            render={({ field, fieldState: { error } }) => (
              <FormControl
                fullWidth
                required
                variant="outlined"
                sx={{ mt: 2 }}
                error={!!error}
              >
                <InputLabel
                  id="select-label-casing-quality"
                  sx={{ color: "#373437", backgroundColor: "white", px: 0.5 }}
                  shrink
                >
                  {t("requirementsTextFields.label2")}
                </InputLabel>
                <Select
                  {...field}
                  labelId="select-label-casing-quality"
                  id="select-casing-quality"
                  label={t("requirementsTextFields.label2")}
                  displayEmpty
                  renderValue={(selected) =>
                    selected
                      ? selected === "XPS"
                        ? t("requirementsTextFields.casingQualityXPS")
                        : t("requirementsTextFields.casingQualityRW")
                      : "*Choose an option (required)*"
                  }
                  MenuProps={{ disableScrollLock: true }}
                  disablePortal
                  sx={{ mb: 1 }}
                >
                  <MenuItem value="XPS">
                    {t("requirementsTextFields.casingQualityXPS")}
                  </MenuItem>
                  <MenuItem value="RW">
                    {t("requirementsTextFields.casingQualityRW")}
                  </MenuItem>
                </Select>
              </FormControl>
            )}
          />

          <Controller
            name="sizeOfUnit"
            control={control}
            rules={validationRules.sizeOfUnit}
            render={({ field, fieldState: { error } }) => {
              // Determine which image to show based on unitType
              const chartImage =
                unitType === AhuType.ENERGY_PRIME_GENIUS
                  ? energyGeniusChart
                  : unitType === AhuType.ENERGY_PRIME_STANDARD
                    ? energyStandardChart
                    : null;

              const handleInfoClick = (event) => {
                setInfoAnchorEl(event.currentTarget);
              };

              const handleInfoClose = () => {
                setInfoAnchorEl(null);
              };

              const open = Boolean(infoAnchorEl);

              return (
                <Box sx={{ mt: 2 }}>
                  <Box
                    sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                  >
                    <FormControl
                      fullWidth
                      required
                      variant="outlined"
                      error={!!error}
                    >
                      <InputLabel
                        id="select-label8"
                        htmlFor="select-size-of-unit"
                        sx={{
                          color: "#373437",
                          backgroundColor: "white",
                          px: 0.5,
                        }}
                        shrink
                      >
                        {t("unitType.label4")}
                      </InputLabel>
                      <Select
                        {...field}
                        labelId="select-label8"
                        id="select-size-of-unit"
                        label={t("unitType.label4")}
                        displayEmpty
                        renderValue={(selected) =>
                          selected
                            ? unitTypesToShow.find((opt) => opt.id === selected)
                              ?.name
                            : "*Choose an option (required)*"
                        }
                        MenuProps={{ disableScrollLock: true }}
                        disablePortal
                        sx={{ mb: 1 }}
                      >
                        {unitTypesToShow.map((option) => (
                          <MenuItem key={option.id} value={option.id}>
                            {option.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {error && (
                        <FormHelperText error>{error.message}</FormHelperText>
                      )}
                    </FormControl>
                    {chartImage && (
                      <IconButton
                        onClick={handleInfoClick}
                        sx={{
                          mt: 1,
                          alignSelf: "flex-start",
                          color: "primary.main",
                        }}
                        aria-label="info"
                      >
                        <InfoIcon />
                      </IconButton>
                    )}
                  </Box>
                  <Popover
                    open={open}
                    anchorEl={infoAnchorEl}
                    onClose={handleInfoClose}
                    anchorOrigin={{
                      vertical: "top",
                      horizontal: "center",
                    }}
                    transformOrigin={{
                      vertical: "center",
                      horizontal: "center",
                    }}
                    PaperProps={{
                      sx: {
                        width: "100vw",
                        height: "100vh",
                        maxWidth: "100vw",
                        maxHeight: "100vh",
                        m: 0,
                        borderRadius: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <IconButton
                        onClick={handleInfoClose}
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          zIndex: 1,
                          backgroundColor: "rgba(255, 255, 255, 0.9)",
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 1)",
                          },
                        }}
                        aria-label="close"
                      >
                        <CloseIcon />
                      </IconButton>
                      <Box
                        sx={{
                          p: 2,
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "auto",
                        }}
                      >
                        <img
                          src={chartImage}
                          alt="Size chart"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            width: "auto",
                            height: "auto",
                            objectFit: "contain",
                            display: "block",
                          }}
                        />
                      </Box>
                    </Box>
                  </Popover>
                </Box>
              );
            }}
          />
          {!isLinePrime && (
            <Controller
              name="unitDivision"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel id="select-label9" sx={{ mt: 1, color: "#373437" }}>
                    {t("unitType.label5")}
                  </InputLabel>
                  <Select
                    {...field}
                    disablePortal
                    MenuProps={{ disableScrollLock: true }}
                    fullWidth
                    sx={{ mt: 1, mb: 1 }}
                    labelId="select-label9"
                    label={t("unitType.label5")}
                    defaultValue={t("unitType.defaultValue5")}
                  >
                    {transport1SizeIds.includes(sizeOfUnit)
                      ? unitDivisionOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))
                      : unitDivisionOptions
                        .filter((opt) => opt.value === "transport3")
                        .map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                  </Select>
                </FormControl>
              )}
            />
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default TypeOfUnit;
